/**
 * useScoringData - Shared hook for player scoring calculations
 *
 * Extracts scoring logic used by both ScoringSlide and any future
 * components that need player scoring data. Single source of truth
 * for calculating points, joker bonuses, placement bonuses, and mult.
 */
import { useCallback } from 'react';
import { PLACEMENT_BONUS, PLAY_TYPE_DISPLAY } from '../shared/core/constants';

/**
 * Hook that provides a function to calculate scoring data for a player.
 *
 * @param {Object} options
 * @param {Object} options.roundStats - Stats object keyed by player index
 * @param {number[]} options.mult - Mult values per player
 * @param {number[]} options.rankings - Player indices in finish order
 * @returns {Function} getPlayerScoringData(playerIndex) => ScoringData
 */
export function useScoringData({ roundStats, mult, rankings }) {
  return useCallback((playerIndex) => {
    const stats = roundStats?.[playerIndex] || {};
    const tricksWonDetails = stats.tricksWonDetails || [];
    const playerMult = mult?.[playerIndex] || 1;
    const numPlayers = rankings?.length || 0;
    const position = rankings?.indexOf(playerIndex) ?? -1;

    // Group tricks by hand type and count them, also track joker bonuses
    const tricksByType = {};
    let totalJokerBonus = 0;
    const jokerBonusesByType = {};

    tricksWonDetails.forEach(trick => {
      const type = trick.playType;
      const points = trick.basePoints || 0;
      if (!tricksByType[type]) {
        tricksByType[type] = { count: 0, pointsEach: points, total: 0 };
      }
      tricksByType[type].count++;
      tricksByType[type].total += points;

      // Track joker bonuses
      if (trick.jokerBonus && trick.jokerBonus !== 0) {
        totalJokerBonus += trick.jokerBonus;
        if (trick.jokerEffects) {
          trick.jokerEffects.forEach(effect => {
            // Support both old (jokerId/jokerIcon) and new (sourceId/sourceIcon) property names
            const id = effect.jokerId || effect.sourceId || effect.effectId;
            const icon = effect.jokerIcon || effect.sourceIcon || effect.effectIcon || '🃏';
            const name = effect.jokerName || effect.sourceName || effect.effectName;
            const description = effect.jokerDescription || effect.sourceDescription;
            const effectType = effect.effectType || 'points';

            // Use effectType in key to keep delay/multiplier effects separate
            // This allows players to see "0 (1. Stich)" and "×3" as separate lines
            const key = `${id || icon}_${effectType}`;

            if (!jokerBonusesByType[key]) {
              jokerBonusesByType[key] = {
                id,
                icon,
                name,
                description,
                total: 0,
                count: 0,
                effectType,
                displayText: effect.displayText
              };
            }
            jokerBonusesByType[key].total += effect.change;
            jokerBonusesByType[key].count++;
            // Keep track of multiplier display text
            if (effect.displayText && effectType === 'multiplier') {
              jokerBonusesByType[key].displayText = effect.displayText;
            }
          });
        }
      }
    });

    // Add round-end joker bonuses (like Flawless Victory)
    const roundEndJokerBonus = stats.roundEndJokerBonus || 0;
    const roundEndJokerEffects = stats.roundEndJokerEffects || [];
    roundEndJokerEffects.forEach(effect => {
      const id = effect.jokerId || effect.sourceId || effect.effectId;
      const icon = effect.jokerIcon || effect.sourceIcon || effect.effectIcon || '💎';
      const name = effect.jokerName || effect.sourceName || effect.effectName;
      const description = effect.jokerDescription || effect.sourceDescription;
      const effectType = effect.effectType || 'roundEnd';
      const key = `${id || icon}_${effectType}`;
      if (!jokerBonusesByType[key]) {
        jokerBonusesByType[key] = { id, icon, name, description, total: 0, count: 0, effectType };
      }
      jokerBonusesByType[key].total += effect.change;
      jokerBonusesByType[key].count++;
    });

    // Trick highscore bonus (Stammtisch)
    const trickHighscoreBonus = stats.trickHighscoreBonus || 0;

    // Seal star bonus (Siegel-Effekte: Die Zwei, Vulkan, Blutegel, Märtyrer, Glücksrad)
    const sealStarBonus = stats.sealStarBonus || 0;

    // Minigame star bonus (Zauberkarten-Minispiele)
    const minigameStarBonus = stats.minigameStarBonus || 0;

    // Add mid-game joker bonuses (e.g., Six Seven ON_CARD_RECEIVED, Schleichweg ON_PLAYER_FINISH)
    const midGameJokerBonus = stats.midGameJokerBonus || 0;
    const midGameJokerEffects = stats.midGameJokerEffects || [];
    midGameJokerEffects.forEach(effect => {
      const id = effect.jokerId || effect.sourceId || effect.effectId;
      const icon = effect.jokerIcon || effect.sourceIcon || effect.effectIcon || '🃏';
      const name = effect.jokerName || effect.sourceName || effect.effectName;
      const description = effect.jokerDescription || effect.sourceDescription;
      const effectType = effect.effectType || 'midGame';
      const key = `${id || icon}_${effectType}`;
      if (!jokerBonusesByType[key]) {
        jokerBonusesByType[key] = { id, icon, name, description, total: 0, count: 0, effectType };
      }
      jokerBonusesByType[key].total += effect.change;
      jokerBonusesByType[key].count++;
    });

    const jokerBonuses = Object.values(jokerBonusesByType);

    // Convert to array and sort by point value (strongest first)
    const groupedTricks = Object.entries(tricksByType)
      .map(([type, data]) => ({
        type,
        config: PLAY_TYPE_DISPLAY[type] || { name: type, emoji: '🎴', points: 0 },
        count: data.count,
        pointsEach: data.pointsEach,
        total: data.total
      }))
      .sort((a, b) => b.config.points - a.config.points);

    // Calculate subtotal from tricks won
    const tricksSubtotal = tricksWonDetails.reduce((sum, t) => sum + (t.basePoints || 0), 0);

    // Placement bonus based on finish position
    const placementBonuses = PLACEMENT_BONUS[numPlayers] || [];
    const placementBonus = position >= 0 && position < placementBonuses.length
      ? placementBonuses[position]
      : 0;

    // Total joker bonus (from tricks + round end + mid-game)
    const totalAllJokerBonuses = totalJokerBonus + roundEndJokerBonus + midGameJokerBonus;

    // Calculate total before mult
    const beforeMult = tricksSubtotal + totalAllJokerBonuses + placementBonus
      + trickHighscoreBonus + sealStarBonus + minigameStarBonus;

    // Use server-calculated values if available (more accurate)
    const serverSubtotal = stats.roundSubtotal;
    const serverMult = stats.playerMult;

    // Use server values if available, otherwise calculate locally
    const subtotal = serverSubtotal !== undefined ? serverSubtotal : beforeMult;
    const effectiveMult = serverMult !== undefined ? serverMult : playerMult;

    // WICHTIG: multBonus immer mit effectiveMult berechnen für Konsistenz
    // Server-multBonus kann inkonsistent sein wenn roundStats vor handleRoundEnd gebroadcastet wird
    const multBonus = Math.floor(subtotal * effectiveMult) - subtotal;

    // Final total = subtotal + mult bonus
    const total = subtotal + multBonus;

    return {
      tricksWonDetails,
      groupedTricks,
      tricksSubtotal,
      jokerBonuses,
      totalJokerBonus: totalAllJokerBonuses,
      placementBonus,
      trickHighscoreBonus,
      sealStarBonus,
      minigameStarBonus,
      subtotal,
      multBonus,
      mult: effectiveMult,
      total,
      position,
      tricksWon: stats.tricksWon || 0
    };
  }, [roundStats, mult, rankings]);
}

export default useScoringData;
